import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Minus, Plus, ShoppingCart, Trash2 } from "lucide-react";
import { useCart } from "../lib/cart";
import { useCheckoutEnabled } from "../lib/checkout";
import PaymentsPausedNotice from "../components/PaymentsPausedNotice";
import { money } from "../lib/format";
import SmartImage from "../components/SmartImage";
import { EmptyState } from "../components/States";
import useSeo from "../hooks/useSeo";

export default function Cart() {
  const { lines, setQty, remove, subtotal, count } = useCart();
  useSeo({ title: "Your cart - NQ Springs & 4x4 Accessories", noindex: true });
  const payments = useCheckoutEnabled();
  const freeOver = 300;
  const shipping = subtotal >= freeOver || subtotal === 0 ? 0 : 19.95;

  if (!payments) {
    return (
      <div className="container-nq section-nq" data-testid="cart-page">
        <p className="eyebrow">Your cart</p>
        <h1 className="mt-2 text-3xl font-bold md:text-4xl">Ordering by phone for now</h1>
        <div className="mt-6 max-w-2xl">
          <PaymentsPausedNotice />
        </div>
      </div>
    );
  }

  return (
    <div className="container-nq section-nq" data-testid="cart-page">
      <p className="eyebrow">Your cart</p>
      <h1 className="mt-2 text-3xl font-bold md:text-4xl">
        Cart {count > 0 && <span className="gold-text">({count})</span>}
      </h1>

      {lines.length === 0 ? (
        <div className="mt-8">
          <EmptyState
            title="Your cart is empty"
            message="Browse our surplus stock and add something to get started."
            action={
              <Link to="/shop" className="btn-gold mt-3" data-testid="cart-empty-shop-link">
                Browse shop <ArrowRight className="h-4 w-4" />
              </Link>
            }
          />
        </div>
      ) : (
        <div className="mt-8 grid gap-8 lg:grid-cols-12">
          <div className="space-y-3 lg:col-span-8">
            {lines.map((l) => (
              <div key={l.slug} className="card-nq flex gap-4 p-4" data-testid="cart-line">
                <Link to={`/shop/${l.slug}`} className="w-24 shrink-0">
                  <SmartImage src={l.image} alt={l.name} ratio="aspect-square" fit="contain" fallbackLabel={l.name} />
                </Link>

                <div className="flex flex-1 flex-col">
                  <Link to={`/shop/${l.slug}`} className="text-sm font-semibold hover:text-[color:var(--nq-gold)]">
                    {l.name}
                  </Link>
                  <p className="mt-1 font-mono text-sm gold-text">{money(l.price)}</p>

                  <div className="mt-auto flex items-center gap-3 pt-3">
                    <div className="flex items-center rounded-lg border" style={{ borderColor: "rgba(255,255,255,0.14)" }}>
                      <button
                        type="button"
                        aria-label="Decrease"
                        onClick={() => setQty(l.slug, l.qty - 1)}
                        className="px-2.5 py-1.5 hover:text-[color:var(--nq-gold)]"
                        data-testid="cart-qty-decrease"
                      >
                        <Minus className="h-3.5 w-3.5" />
                      </button>
                      <span className="w-8 text-center font-mono text-xs" data-testid="cart-qty">{l.qty}</span>
                      <button
                        type="button"
                        aria-label="Increase"
                        onClick={() => setQty(l.slug, l.qty + 1)}
                        className="px-2.5 py-1.5 hover:text-[color:var(--nq-gold)]"
                        data-testid="cart-qty-increase"
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>

                    <button
                      type="button"
                      onClick={() => remove(l.slug)}
                      className="flex items-center gap-1.5 text-xs text-[color:var(--nq-stone)] hover:text-[color:var(--destructive)]"
                      data-testid="cart-remove-button"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Remove
                    </button>
                  </div>
                </div>

                <p className="font-mono text-sm font-semibold">{money(l.price * l.qty)}</p>
              </div>
            ))}
          </div>

          <div className="lg:col-span-4">
            <div className="card-nq sticky top-28 p-6">
              <h2 className="text-lg font-semibold">Order summary</h2>
              <dl className="mt-4 space-y-2.5 text-sm">
                <div className="flex justify-between">
                  <dt className="text-[color:var(--nq-stone)]">Subtotal</dt>
                  <dd className="font-mono" data-testid="cart-subtotal">{money(subtotal)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-[color:var(--nq-stone)]">Shipping (standard)</dt>
                  <dd className="font-mono">{shipping === 0 ? "FREE" : money(shipping)}</dd>
                </div>
                <div
                  className="flex justify-between border-t pt-2.5 text-base font-bold"
                  style={{ borderColor: "rgba(255,255,255,0.08)" }}
                >
                  <dt>Total</dt>
                  <dd className="font-mono gold-text" data-testid="cart-total">{money(subtotal + shipping)}</dd>
                </div>
              </dl>

              {subtotal < freeOver && (
                <p className="mt-3 text-xs text-[color:var(--nq-stone)]">
                  Spend {money(freeOver - subtotal)} more for free shipping.
                </p>
              )}

              <Link to="/checkout" className="btn-gold mt-5 w-full px-6 py-3" data-testid="cart-checkout-button">
                <ShoppingCart className="h-4 w-4" /> Proceed to checkout
              </Link>
              <Link to="/shop" className="btn-outline-nq mt-2 w-full px-6 py-3">
                Continue shopping
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
